/**
 * Registration hub for the built-in primitives. Importing this module (done for its side effect by
 * `StepBodyRenderer`) registers every primitive into the registry mechanics in `./registry`.
 *
 * Every step body is a composition of FINE primitives (`stakeholder-roster`, `discovery-questions`, …),
 * opted into per-step by a seeded `step_layout` block that lists their keys in render order. Heavy fine
 * primitives are lazy-loaded (see `lazyPrimitive`) so their deps stay out of this static graph.
 */
import { registerPrimitive } from './registry';
import { lazyPrimitive } from './lazy';

// Fine primitives, grouped by the body they were extracted from. Statically imported ones are
// jest-/bundle-safe; heavy ones (worker/ESM-backed) are lazy-loaded below to stay out of this graph.
import { StakeholderRoster } from './prepare';
import { ConversationGuide, DiscoveryQuestions } from './propose';
import { CommentsBlock } from './workshop';
import { DeepDivePanelBlock } from './deepdive';
import { GapAnalysis } from './recommendation';
import { FaqBlock } from './faq';

let registered = false;

/** Register the built-in primitives. Idempotent; safe to call from multiple import sites. */
export function registerCorePrimitives(): void {
  if (registered) return;
  registered = true;

  // --- Fine primitives: Prepare ---
  registerPrimitive('stakeholder-roster', StakeholderRoster);

  // --- Fine primitives: Profiling (lazy: ConnectCard pulls the install-base parse worker) ---
  registerPrimitive('connect-tools', lazyPrimitive(() => import('./profiling')));

  // --- Fine primitives: Propose ---
  registerPrimitive('conversation-guide', ConversationGuide);
  registerPrimitive('discovery-questions', DiscoveryQuestions);

  // --- Fine primitives: Workshop (session-deck lazy: pptx/PDF deck builders) ---
  registerPrimitive('session-deck', lazyPrimitive(() => import('./workshopDeck')));
  registerPrimitive('comments', CommentsBlock);

  // --- Fine primitives: Deep dive (comments reuses the shared 'comments' primitive) ---
  registerPrimitive('deep-dive-panel', DeepDivePanelBlock);

  // --- Fine primitives: Recommendation (report-editor lazy: report/PDF export subsystem;
  //     comments reuses the shared 'comments' primitive) ---
  registerPrimitive('gap-analysis', GapAnalysis);
  registerPrimitive('report-editor', lazyPrimitive(() => import('./reportEditor')));

  // --- Optional, opt-in sections (not in any default layout; added per-step by an author) ---
  registerPrimitive('faq', FaqBlock);
}

registerCorePrimitives();
